import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill } from "@/components/StatusPill";
import { apiClient, auth, formatMoney } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EditPartnerDialog } from "@/components/EditPartnerDialog";
import { toast } from "sonner";
import { ChevronLeft, Mail, Phone, KeyRound, Briefcase, ArrowRight, Pencil } from "lucide-react";

/**
 * Drill-down page used by both admin and employee to view a single partner
 * and the client applications / referrals attributed to that partner.
 *
 * Mode is inferred from the URL prefix:
 *   /admin/partners/:id    -> admin scope
 *   /employee/partners/:id -> employee scope (server enforces scope)
 */
export default function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = auth.getRole();
  const isAdmin = role === "admin";
  const backTo = isAdmin ? "/admin/dashboard" : "/employee/dashboard";
  const referralBase = isAdmin ? "/admin/referrals" : "/employee/referrals";

  const [partner, setPartner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    try {
      const partnerUrl = isAdmin ? `/admin/partners/${id}` : `/employee/partners/${id}`;
      const [p, r] = await Promise.all([
        apiClient.get(partnerUrl),
        apiClient.get(`/referrals?partner_id=${id}`),
      ]);
      setPartner(p.data);
      setReferrals(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load partner");
      if (err?.response?.status === 404 || err?.response?.status === 403) {
        navigate(backTo);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin && role !== "employee") {
      navigate(isAdmin ? "/admin/login" : "/employee/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAdmin, role, navigate, backTo]);

  if (loading) {
    return (
      <DashboardShell role={role} title="Partner">
        <div className="p-10 text-center text-slate-500">Loading…</div>
      </DashboardShell>
    );
  }

  if (!partner) return null;

  const totalRequested = referrals.reduce((s, r) => s + (Number(r.funding_amount_needed) || 0), 0);
  const totalFunded = referrals.reduce((s, r) => s + (Number(r.funding_amount) || 0), 0);

  return (
    <DashboardShell
      role={role}
      title={partner.full_name}
      subtitle="Clients this partner has submitted, with status and commission."
      actions={
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <Button
              onClick={() => setEditOpen(true)}
              data-testid={TIDS.adminPartnerEdit + "-detail"}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
            >
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Button>
          ) : null}
          <Link to={backTo}>
            <Button variant="outline" className="rounded-full" data-testid={TIDS.partnerDetailBack}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          </Link>
        </div>
      }
    >
      <div data-testid={TIDS.partnerDetailView} className="space-y-8">
        <Breadcrumbs
          items={
            isAdmin
              ? [
                  { label: "Employees", to: "/admin/dashboard" },
                  partner.recruited_by_employee
                    ? { label: partner.recruited_by_employee.full_name, to: `/admin/employees/${partner.recruited_by_employee.id}` }
                    : { label: "Unassigned" },
                  { label: partner.full_name },
                ]
              : [
                  { label: "My partners", to: "/employee/dashboard" },
                  { label: partner.full_name },
                ]
          }
        />
        {/* Partner profile */}
        <Card className="rounded-2xl border-slate-200 shadow-sm p-6 md:p-7">
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <Mail className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Email</div>
                <div className="text-sm text-slate-900 break-all">{partner.email}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <Phone className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Mobile</div>
                <div className="text-sm text-slate-900">{partner.mobile}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                <KeyRound className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Referral code</div>
                <div className="text-sm font-mono text-slate-900">{partner.referral_code}</div>
              </div>
            </div>
            {partner.company_name ? (
              <div className="flex items-start gap-3 sm:col-span-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                  <Briefcase className="h-4 w-4" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Company</div>
                  <div className="text-sm text-slate-900">{partner.company_name}</div>
                </div>
              </div>
            ) : null}
          </div>
        </Card>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-4 md:gap-6">
          <Card className="rounded-xl border-slate-200 shadow-sm p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Clients</div>
            <div className="mt-2 text-3xl font-heading font-medium text-slate-900">{referrals.length}</div>
          </Card>
          <Card className="rounded-xl border-slate-200 shadow-sm p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Requested</div>
            <div className="mt-2 text-3xl font-heading font-medium text-slate-900">{formatMoney(totalRequested)}</div>
          </Card>
          <Card className="rounded-xl border-slate-200 shadow-sm p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Funded</div>
            <div className="mt-2 text-3xl font-heading font-medium text-slate-900">{formatMoney(totalFunded)}</div>
          </Card>
        </div>

        {/* Clients table */}
        <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h2 className="text-lg font-medium text-slate-900">Client applications</h2>
            <p className="text-sm text-slate-500">{referrals.length} total</p>
          </div>
          {referrals.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No clients yet from this partner.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Business</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Requested</TableHead>
                  <TableHead className="text-right">Funded</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((r) => (
                  <TableRow key={r.id} data-testid={TIDS.partnerDetailClientRow}>
                    <TableCell className="font-medium text-slate-900">{r.business_name}</TableCell>
                    <TableCell className="text-slate-700">
                      {r.business_owner_name}
                      <div className="text-xs text-slate-500">{r.owner_email}</div>
                    </TableCell>
                    <TableCell><StatusPill status={r.status} /></TableCell>
                    <TableCell className="text-right text-slate-700">{formatMoney(r.funding_amount_needed)}</TableCell>
                    <TableCell className="text-right text-slate-700">
                      {r.funding_amount ? formatMoney(r.funding_amount) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`${referralBase}/${r.id}`}>
                        <Button variant="ghost" size="sm" className="hover:text-blue-700">
                          Open <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {isAdmin ? (
        <EditPartnerDialog
          partner={partner}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={load}
        />
      ) : null}
    </DashboardShell>
  );
}
